import { memo, ReactNode } from 'react'

import { useDataGridContext } from '../hooks/useDataGridContext'
import { Cell, Id } from '../types'
import { ModalRender, TableCell } from './TableCell'

type BodyRowProps<TCol extends { id: Id }, TRow extends { id: Id }> = {
    cols: TCol[]

    row: TRow
    rowHeader?: (row: TRow) => ReactNode
    rowClassName?: (row: TRow) => string

    children: (row: TRow, col: TCol, isSelected: boolean) => ReactNode
    cellClassName?: (row: TRow, col: TCol) => string
    cellDataTest?: string
    height?: number
    gridTemplateColumns: string
    modalContentRender?: ModalRender
    disableCellHover?: boolean
}

const anyCellIdInRowIsSelected = (rowId: string, selectedCells: Cell[]) => [...selectedCells].some(cell => cell.rowId === rowId)

const BodyRowComponent = <TCol extends { id: Id }, TRow extends { id: Id }>({
    cols,
    row,
    height,
    rowHeader,
    children,
    rowClassName,
    modalContentRender,
    cellClassName,
    cellDataTest,
    gridTemplateColumns,
    disableCellHover = false,
}: BodyRowProps<TCol, TRow>) => {
    const { selectedCells } = useDataGridContext()
    const isRowSelected = anyCellIdInRowIsSelected(String(row.id), selectedCells)

    return (
        <div id={`row-${row.id}`} className={`group grid ${rowClassName?.(row)}`} style={{ gridTemplateColumns }} data-test="body-row">
            {rowHeader && (
                <div
                    style={{ height }}
                    role="rowheader"
                    className={`${isRowSelected ? '!bg-surface-strong' : ''} sticky left-0 flex items-center truncate border-b border-r-2 bg-surface-base`}
                >
                    {rowHeader(row)}
                </div>
            )}

            {cols.map(col => (
                <TableCell
                    key={`${row.id}-${col.id}`}
                    col={col}
                    row={row}
                    dataTest={cellDataTest}
                    height={height}
                    isRowSelected={isRowSelected}
                    cellClassName={cellClassName}
                    modalContentRender={modalContentRender}
                    disableCellHover={disableCellHover}
                >
                    {children}
                </TableCell>
            ))}
        </div>
    )
}

export const BodyRow = memo(BodyRowComponent) as typeof BodyRowComponent
